<mat-card appearance="outlined" class="cc-panel">
  <mat-card-content>
    <h2 class="section-title">
      <app-icon name="scale" [size]="18" />
      <span>Credit Committee</span>
    </h2>

    @if (isLoading()) {
      <div class="cc-panel__loading">
        <span class="sg-skeleton sg-skeleton--text sg-skeleton--w60"></span>
        <span class="sg-skeleton sg-skeleton--text sg-skeleton--w40"></span>
      </div>
    } @else if (loadError()) {
      <div class="sg-empty sg-empty--bordered sg-empty--error">
        <app-icon name="x-circle" [size]="32" />
        <p class="sg-empty__title">Couldn't load resolutions</p>
        <button mat-stroked-button type="button" (click)="load()">
          <app-icon name="refresh" [size]="16" /> Try again
        </button>
      </div>
    } @else if (resolutions().length === 0) {
      <p class="muted" data-testid="cc-none">
        No Credit Committee resolution for this matter yet. A resolution is required where the
        committee must act together — at least the quorum number of members must approve. A member
        opens it, under their own name.
      </p>
    } @else {
      <ul class="cc-list">
        @for (r of resolutions(); track r.short_id) {
          <li class="cc-list__item" [class.cc-list__item--passed]="r.status === 'PASSED'">
            <div class="cc-list__head">
              <span class="cc-list__action">{{ r.action }}</span>
              <app-status-chip [status]="r.status" />
            </div>

            <p class="cc-list__rationale">{{ r.rationale }}</p>

            <div class="cc-list__progress" [class.cc-list__progress--met]="quorumMet(r)">
              <app-icon [name]="quorumMet(r) ? 'check-circle' : 'users'" [size]="16" />
              <span>{{ progressLabel(r) }}</span>
            </div>

            @if (r.status === 'PASSED' && r.cc_ref) {
              <div class="cc-list__ref">
                <span class="cc-list__ref-label">Reference</span>
                <code class="cc-list__ref-value">{{ r.cc_ref }}</code>
                <button
                  mat-icon-button
                  type="button"
                  [attr.aria-label]="'Copy reference ' + r.cc_ref"
                  matTooltip="Copy reference"
                  (click)="copyRef(r.cc_ref)"
                >
                  <app-icon name="copy" [size]="16" />
                </button>
              </div>
            }

            @if (r.status === 'WITHDRAWN' && r.withdrawn_at) {
              <!-- FLOW-405 (D4): who withdrew it, when and why, from the record. -->
              <p class="cc-list__withdrawn" data-testid="cc-withdrawn">
                Withdrawn by {{ r.withdrawn_by_name }} on
                {{ r.withdrawn_at | date: 'mediumDate' }} — {{ r.withdrawal_reason }}
              </p>
            }

            @if (r.status === 'OPEN') {
              <div class="cc-list__acts">
                <button
                  mat-flat-button
                  color="primary"
                  type="button"
                  class="cc-list__approve"
                  [disabled]="!isCcMember() || approvingId() === r.short_id"
                  [matTooltip]="
                    isCcMember() ? 'Record your approval' : 'Requires Credit Committee membership'
                  "
                  (click)="approve(r)"
                >
                  <app-icon name="check" [size]="16" />
                  <span>{{ approvingId() === r.short_id ? 'Loading…' : 'Approve' }}</span>
                </button>
                <!-- DOC-82-A §5: *Withdraw* is a member's act; no one else is offered it. -->
                @if (isCcMember()) {
                  <button
                    mat-stroked-button
                    type="button"
                    (click)="toggleWithdraw(r)"
                    data-testid="cc-withdraw-toggle"
                  >
                    Withdraw
                  </button>
                }
              </div>
              @if (isCcMember() && withdrawingId() === r.short_id) {
                <form [formGroup]="withdrawForm" (ngSubmit)="withdraw(r)" class="cc-open-form">
                  <mat-form-field appearance="outline">
                    <mat-label>Reason for the withdrawal</mat-label>
                    <textarea matInput formControlName="reason" rows="2"></textarea>
                    @if (
                      withdrawForm.controls.reason.touched && withdrawForm.controls.reason.invalid
                    ) {
                      <mat-error>A written reason is required</mat-error>
                    }
                  </mat-form-field>
                  <div class="cc-open-form__actions">
                    <button mat-stroked-button type="button" (click)="toggleWithdraw(r)">
                      Cancel
                    </button>
                    <button
                      mat-flat-button
                      color="warn"
                      type="submit"
                      [disabled]="isWithdrawing()"
                      data-testid="cc-withdraw-submit"
                    >
                      {{ isWithdrawing() ? 'Withdrawing…' : 'Withdraw the resolution' }}
                    </button>
                  </div>
                </form>
              }
            }

            @if (r.approvals.length) {
              <ul class="cc-list__approvals">
                @for (a of r.approvals; track a.short_id) {
                  <li>
                    <span class="cc-list__approval-check"
                      ><app-icon name="check" [size]="14"
                    /></span>
                    <span>{{ a.member_name }}</span>
                    <span class="cc-list__approval-date">{{
                      a.created_at | date: 'mediumDate'
                    }}</span>
                  </li>
                }
              </ul>
            }
          </li>
        }
      </ul>
    }

    <!--
      Open a resolution — a member's own act (DOC-82-A §5): offered to a member
      only; everyone else reads the panel. A committee resolution is a one-time
      authority act:
      • no resolution yet  → primary "Open resolution" CTA
      • one already OPEN    → no affordance (one in flight at a time)
      • one already PASSED  → demoted, secondary "supersede" action behind a
        divider, so the passed result stays the dominant signal (P1 defect fix)
    -->
    @if (isCcMember() && !isLoading() && !loadError() && !hasOpenResolution()) {
      @let superseding = hasPassedResolution();
      <div class="cc-panel__open" [class.cc-panel__open--supersede]="superseding">
        @if (showOpenForm()) {
          <form [formGroup]="openForm" (ngSubmit)="openResolution()" class="cc-open-form">
            @if (superseding) {
              <p class="cc-panel__supersede-note">
                This matter already has a passed resolution. Opening another records a fresh,
                separate committee decision — only do this if the prior resolution no longer
                applies.
              </p>
            }
            <mat-form-field appearance="outline">
              <mat-label>Action being resolved</mat-label>
              <input
                matInput
                formControlName="action"
                placeholder="e.g. Approve facility limit of ZAR 2,000,000"
              />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Rationale</mat-label>
              <textarea
                matInput
                formControlName="rationale"
                rows="3"
                placeholder="Why the committee should approve this"
              ></textarea>
            </mat-form-field>
            <div class="cc-open-form__actions">
              <button mat-stroked-button type="button" (click)="toggleOpenForm()">Cancel</button>
              <button
                mat-flat-button
                color="primary"
                type="submit"
                [disabled]="isOpening()"
                data-testid="cc-open-submit"
              >
                <app-icon name="scale" [size]="16" />
                <span>{{ isOpening() ? 'Opening…' : 'Open resolution' }}</span>
              </button>
            </div>
          </form>
        } @else if (superseding) {
          <button
            mat-button
            type="button"
            class="cc-panel__supersede-btn"
            matTooltip="Open a new resolution to supersede the passed one"
            (click)="toggleOpenForm()"
            data-testid="cc-open-toggle"
          >
            <app-icon name="scale" [size]="16" />
            <span>Open a new resolution</span>
          </button>
        } @else {
          <button
            mat-flat-button
            color="primary"
            type="button"
            matTooltip="Open a resolution for the committee to act on"
            (click)="toggleOpenForm()"
            data-testid="cc-open-toggle"
          >
            <app-icon name="scale" [size]="16" />
            <span>Open resolution</span>
          </button>
        }
      </div>
    }
  </mat-card-content>
</mat-card>
